import React, { useState, useRef, useEffect } from 'react';
import EmojiPicker, { Theme, EmojiClickData } from 'emoji-picker-react';
import { Smile } from 'lucide-react';

interface CommentEmojiPickerProps {
  onSelect: (emoji: string) => void;
  className?: string;
}

const CommentEmojiPicker: React.FC<CommentEmojiPickerProps> = ({ onSelect, className }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const togglePicker = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const onEmojiClick = (emojiData: EmojiClickData, event: MouseEvent) => {
    event.stopPropagation();
    onSelect(emojiData.emoji);
    setIsOpen(false);
  };

  return (
    <div className={`relative inline-block text-left ${className}`} ref={containerRef}>
      <button 
        type="button"
        className={`p-1 rounded-md transition-all duration-200 flex items-center justify-center group ${isOpen ? 'text-[#7C6CF7] bg-primary-subtle' : 'text-neutral-medium hover:text-[#7C6CF7] hover:bg-primary-subtle'}`}
        title="Add Reaction"
        onClick={togglePicker}
      >
        <Smile className="w-4 h-4" />
      </button>

      {isOpen && (
        <React.Fragment>
          <style>{`
            .custom-emoji-picker .EmojiPickerReact {
              --epr-bg-color: #131720 !important;
              --epr-category-label-bg-color: #131720 !important;
              --epr-hover-bg-color: rgba(38, 132, 255, 0.1) !important;
              --epr-focus-bg-color: rgba(38, 132, 255, 0.1) !important;
              --epr-search-input-bg-color: #1E2230 !important;
              --epr-text-color: #94A3B8 !important;
              border: 1px solid #3b4255 !important;
              --epr-category-icon-active-color: #7C6CF7 !important;
              --epr-search-border-color: #2b2b36 !important;
            }
            .custom-emoji-picker .epr-emoji-category-label {
              font-size: 13px !important;
              font-weight: 500 !important;
              padding-top: 8px !important;
              padding-bottom: 8px !important;
            }
            .custom-emoji-picker .EmojiPickerReact .epr-search-container input {
              border: 1px solid #2b2b36 !important;
              outline: none !important;
              box-shadow: none !important;
              background-color: #1E2230 !important;
             
            }
            .custom-emoji-picker .EmojiPickerReact .epr-search-container input:focus {
              border: 1px solid #7C6CF7 !important;
              outline: none !important;
              box-shadow: none !important;
            }
          `}</style>
          <div 
            className="absolute z-[9999] bottom-full right-0 mb-2 animate-in fade-in zoom-in duration-200 shadow-2xl rounded-xl custom-emoji-picker"
            onClick={(e) => e.stopPropagation()}
          >
            <EmojiPicker 
              onEmojiClick={onEmojiClick}
              theme={Theme.DARK}
              lazyLoadEmojis={true}
              searchDisabled={false}
              skinTonesDisabled={true}
              width={300}
              height={400}
            />
          </div>
        </React.Fragment>
      )}
    </div>
  );
};

export default CommentEmojiPicker;
